import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { components as c } from "@/components";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useSelectDiaryLabels } from "@/features/diaries/api/selectDiaryLabels";
import { searchForTemplateAiWizardNavigation } from "@/features/ai-builder/useTemplateAiDraftStore";
import { DIARY_KEYS, DIARY_KEY_LABELS } from "@/features/templates/constants/diaryKeys";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useAuth } from "@/utils/auth";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { templateSettingsSchema, type DiaryKeyT, type TemplateSettingsSchemaT } from "@patient-watch/types";
import { CardDescription } from "@patient-watch/ui/card";

const adminFormSchema = z.object({
    diary_labels: z.array(z.string()),
    quickstart_key: z.string(),
});

type AdminFormValues = z.infer<typeof adminFormSchema>;

/**
 * Admin-only step, at the end: study or group labels and the diary type.
 * Only shown in admin view. Saves through the same wizard finish as the other steps.
 */
const TemplateLabelsStep = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { templateId } = Route.useParams();
    const search = Route.useSearch() as Partial<TemplateSettingsSchemaT>;
    const { currentStep, setCurrentStep, getPreviousStepRoute } = useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );
    const { data: labelOptions = [] } = useSelectDiaryLabels();

    const defaultValues = useMemo<AdminFormValues>(
        () => ({
            diary_labels: (search.diary_labels as string[] | undefined) ?? [],
            quickstart_key: (search.quickstart_key as string | undefined) ?? "",
        }),
        // Only read the search once on mount; the form owns the values after that.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const form = useForm<AdminFormValues>({
        resolver: zodResolver(adminFormSchema),
        defaultValues,
    });

    useEffect(() => {
        setCurrentStep("labels");
    }, [setCurrentStep]);

    const quickstartKey = form.watch("quickstart_key");

    const toSettings = (values: AdminFormValues): Partial<TemplateSettingsSchemaT> => ({
        diary_labels: values.diary_labels,
        quickstart_key: values.quickstart_key as DiaryKeyT,
    });

    const goBack = () => {
        const values = form.getValues();
        const route = getPreviousStepRoute(currentStep, { templateId });
        if (!route) return;
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", {
            ...search,
            ...toSettings(values),
        });
        if (!payload) return;
        navigate({ ...route, search: payload as never });
    };

    const onSave = form.handleSubmit((values) => {
        finalize({ ...search, ...toSettings(values) });
    });

    return (
        <div className="space-y-8">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Labels and diary type</c.ui.CardTitle>
                    <CardDescription>
                        Study or group labels and the diary type. These apply to diaries created from this template.
                    </CardDescription>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <c.ui.Form {...form}>
                        <form
                            onSubmit={(event: FormEvent<HTMLFormElement>) => event.preventDefault()}
                            className="space-y-4"
                        >
                            <div className="space-y-2">
                                <fc.MultiSelect
                                    name="diary_labels"
                                    label="Diary labels (study or group names)"
                                    trigger="Select labels"
                                    options={labelOptions.map((l) => ({
                                        value: l.label_name,
                                        label: l.label_name,
                                    }))}
                                />
                                <p className="text-muted-foreground text-sm">
                                    <Link to="/admin/labels" className="text-primary hover:underline">
                                        Manage labels and links
                                    </Link>
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Diary type (quickstart key)</Label>
                                <Select
                                    value={quickstartKey}
                                    onValueChange={(val) => form.setValue("quickstart_key", val as DiaryKeyT)}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {DIARY_KEYS.map((key) => (
                                            <SelectItem key={key} value={key}>
                                                {DIARY_KEY_LABELS[key] ?? key}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </form>
                    </c.ui.Form>
                </c.ui.CardContent>
            </c.ui.Card>

            <TemplateStepFooter
                onPrimaryClick={() => void onSave()}
                onBack={goBack}
                primaryAction="save"
                primaryLabel="Save template"
                isLoadingPrimary={isSubmitting}
                disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
            />
        </div>
    );
};

const adminSearchSchema = templateSettingsSchema.partial();

export const Route = createFileRoute("/_app/templates/$templateId/labels")({
    component: TemplateLabelsStep,
    validateSearch: (search) => adminSearchSchema.parse(search),
});
